/**
 * iter706 — Monthly inter-branch view.
 *
 * Two complementary lenses on top of the existing inter-branch ledger:
 *
 *   1. **Pair view** — pick Branch A + Branch B + a month → see the
 *      pair's opening balance, every txn / settlement / transfer in
 *      that window, and the closing balance. Use this to walk an
 *      accountant through how the closing number was reached.
 *
 *   2. **Matrix view** — a square grid of every branch on rows AND
 *      columns. Cell `(A, B)` shows the closing balance for that pair
 *      at month end (signed from A's perspective). Use this as the
 *      one-glance "anywhere I still owe / am still owed" dashboard.
 *
 * Both lenses sit on top of the new `/api/reports/inter-branch/monthly-matrix`
 * + the extended `/api/reports/inter-branch/statement` endpoints.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ArrowRight, Calendar, Grid3x3, Loader2, ChevronDown, Wallet, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useSettleAll } from '@/hooks/useSettleAll';
import { cur } from '@/lib/currency';
import { useWebSocket } from '@/hooks/useWebSocket';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const KIND_LABEL = {
  expense: 'Expense',
  salary: 'Salary',
  supplier_payment: 'Supplier pay',
  purchase: 'Purchase',
  settlement: 'Settlement',
  transfer: 'Cash transfer',
};
const KIND_TONE = {
  settlement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  transfer: 'bg-amber-50 text-amber-800 border-amber-200',
  expense: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  salary: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  supplier_payment: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  purchase: 'bg-zinc-50 text-zinc-700 border-zinc-200',
};

export default function MonthlyInterBranchView({ branches }) {
  const [mode, setMode] = useState('pair'); // 'pair' | 'matrix' | 'trend'
  const [month, setMonth] = useState(defaultMonth);
  const [branchA, setBranchA] = useState('');
  const [branchB, setBranchB] = useState('');
  const [pairLoading, setPairLoading] = useState(false);
  const [pairData, setPairData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixData, setMatrixData] = useState(null);
  // Matrix scope: 'month' (period-bounded) or 'all' (all-time cumulative net,
  // matching the Branch Summary scope exactly).
  const [matrixScope, setMatrixScope] = useState('month');

  // Auto-pick the first two branches as a sensible default for pair view.
  useEffect(() => {
    if (!branchA && branches.length >= 1) setBranchA(branches[0].id);
    if (!branchB && branches.length >= 2) setBranchB(branches[1].id);
  }, [branches, branchA, branchB]);

  const loadPair = async () => {
    if (!branchA || !branchB || branchA === branchB) {
      toast.error('Pick two different branches');
      return;
    }
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    setPairLoading(true);
    try {
      const r = await api.get('/reports/inter-branch/statement', {
        params: {
          branch_a: branchA, branch_b: branchB,
          start_date: `${month}-01`,
          end_date: `${month}-${String(last).padStart(2, '0')}`,
        },
      });
      setPairData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load statement');
    } finally { setPairLoading(false); }
  };

  const loadMatrix = async () => {
    setMatrixLoading(true);
    try {
      const r = await api.get('/reports/inter-branch/monthly-matrix', { params: { month, scope: matrixScope } });
      setMatrixData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load matrix');
    } finally { setMatrixLoading(false); }
  };

  // iter845s — Trend view: 12-month rolling closing balance per pair.
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const loadTrend = async () => {
    setTrendLoading(true);
    try {
      const { data } = await api.get('/reports/inter-branch/monthly-trend?months=12');
      setTrendData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load trend');
    } finally { setTrendLoading(false); }
  };

  // Auto-load whichever view is currently active when month / branches change.
  useEffect(() => {
    if (mode === 'pair' && branchA && branchB && branchA !== branchB) loadPair();
    if (mode === 'matrix') loadMatrix();
    if (mode === 'trend' && !trendData) loadTrend();
  }, [mode, month, branchA, branchB, matrixScope]);

  // Live Bridge: refresh the active view instantly on `balance_updated`.
  useWebSocket((msg) => {
    if (msg?.type !== 'balance_updated') return;
    if (mode === 'pair' && branchA && branchB && branchA !== branchB) loadPair();
    else if (mode === 'matrix') loadMatrix();
    else if (mode === 'trend') loadTrend();
  });

  return (
    <Card className="border-2 border-indigo-200" data-testid="monthly-inter-branch-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <Calendar size={16} className="text-indigo-600" /> Monthly settlement view
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-nets opposite balances per branch pair, per month. Cash transfers count as settlements (configured by you).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9 w-[150px] text-sm"
              data-testid="monthly-month-picker"
            />
            <div className="inline-flex rounded-lg border border-input p-0.5 bg-secondary/40">
              <button
                type="button"
                onClick={() => setMode('pair')}
                className={`px-3 py-1 rounded text-xs font-medium ${mode === 'pair' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}
                data-testid="monthly-mode-pair"
              >Pair view</button>
              <button
                type="button"
                onClick={() => setMode('matrix')}
                className={`px-3 py-1 rounded text-xs font-medium ${mode === 'matrix' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}
                data-testid="monthly-mode-matrix"
              >Matrix view</button>
              <button
                type="button"
                onClick={() => setMode('trend')}
                className={`px-3 py-1 rounded text-xs font-medium ${mode === 'trend' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}
                data-testid="monthly-mode-trend"
              >12-mo trend</button>
            </div>
          </div>
        </div>

        {mode === 'pair' ? (
          <PairView
            branches={branches}
            branchA={branchA} setBranchA={setBranchA}
            branchB={branchB} setBranchB={setBranchB}
            loading={pairLoading}
            data={pairData}
            onReload={loadPair}          />
        ) : mode === 'matrix' ? (
          <MatrixView loading={matrixLoading} data={matrixData} month={month} onReload={loadMatrix} scope={matrixScope} setScope={setMatrixScope} />
        ) : (
          <TrendView loading={trendLoading} data={trendData} onReload={loadTrend} />
        )}
      </CardContent>
    </Card>
  );
}

// ── Pair view ─────────────────────────────────────────────────────────

function PairView({ branches, branchA, setBranchA, branchB, setBranchB, loading, data, onReload }) {
  const aName = data?.branch_a?.name;
  const bName = data?.branch_b?.name;
  const opening = data?.opening_balance || 0;
  const closing = data?.closing_balance || 0;
  const owes = data?.currently_owes;

  // Aggregate by kind for the chips header.
  const totals = useMemo(() => {
    const t = { expense: 0, salary: 0, supplier_payment: 0, purchase: 0, settlement: 0, transfer: 0 };
    for (const r of (data?.rows || [])) {
      if (t[r.kind] != null) t[r.kind] += Number(r.amount || 0);
    }
    return t;
  }, [data]);

  // iter925 — True Mode Split: use the engine's EXACT historical cash/bank
  // lanes (opening/closing) + window activity/settlement splits instead of the
  // old activity-derived estimate. Bank Flow == the settlements' bank portion.
  const obm = data?.opening_by_mode || { cash: 0, bank: 0 };
  const cbm = data?.closing_by_mode || { cash: 0, bank: 0 };
  const abm = data?.activity_by_mode || { cash: 0, bank: 0 };
  const sbm = data?.settlements_by_mode || { cash: 0, bank: 0 };
  const modeLine = (cash, bank) => (
    (Math.abs(cash) > 0 || Math.abs(bank) > 0)
      ? `Cash: ${fmt(cash)} · Bank: ${fmt(bank)}`
      : null
  );

  return (
    <div className="space-y-3" data-testid="monthly-pair-view">
      {/* Branch pickers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Branch A</Label>
          <Select value={branchA || ''} onValueChange={setBranchA}>
            <SelectTrigger className="h-9 text-sm" data-testid="monthly-branch-a"><SelectValue placeholder="Pick branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Branch B</Label>
          <Select value={branchB || ''} onValueChange={setBranchB}>
            <SelectTrigger className="h-9 text-sm" data-testid="monthly-branch-b"><SelectValue placeholder="Pick branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button onClick={onReload} disabled={loading} className="w-full h-9 text-sm" data-testid="monthly-pair-reload">
            {loading && <Loader2 size={14} className="animate-spin mr-1" />} Refresh
          </Button>
        </div>
      </div>

      {/* Opening / activity / closing chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Opening balance" value={opening} hint={`carried into ${data?.period?.start_date || ''}`} tone={opening === 0 ? 'zero' : (opening > 0 ? 'pos' : 'neg')} testId="monthly-stat-opening" sub={modeLine(obm.cash, obm.bank)} />
        <Stat label="Cross-branch txns" value={totals.expense + totals.salary + totals.supplier_payment + totals.purchase} hint={`${(data?.row_count || 0)} rows in window`} tone="info" testId="monthly-stat-txns" sub={modeLine(abm.cash, abm.bank)} />
        <Stat label="Settlements + transfers" value={totals.settlement + totals.transfer} hint={`${totals.settlement.toFixed(0)} settled / ${totals.transfer.toFixed(0)} transferred`} tone="info" testId="monthly-stat-settled" sub={modeLine(sbm.cash, sbm.bank)} />
        <Stat label="Closing balance" value={closing} hint={owes ? `${owes.from} owes ${owes.to}` : 'fully settled'} tone={closing === 0 ? 'zero' : (closing > 0 ? 'pos' : 'neg')} testId="monthly-stat-closing" big sub={modeLine(cbm.cash, cbm.bank)} />
      </div>

      {/* Plain-english closing-balance derivation */}
      {data?.rows?.length > 0 && (
        <div className="rounded-lg bg-secondary/40 border border-border p-3 text-xs leading-relaxed" data-testid="monthly-pair-derivation">
          <p className="text-muted-foreground">
            <strong>How the closing balance was reached:</strong>
            {' '}Opening <strong>{fmt(opening)}</strong>
            {totals.expense + totals.salary + totals.supplier_payment + totals.purchase > 0 && <> + activity <strong>{fmt(totals.expense + totals.salary + totals.supplier_payment + totals.purchase)}</strong></>}
            {totals.settlement > 0 && <> − settlements <strong>{fmt(totals.settlement)}</strong></>}
            {totals.transfer > 0 && <> − cash transfers <strong>{fmt(totals.transfer)}</strong></>}
            {' '}= closing <strong>{fmt(closing)}</strong>.
            {' '}Sign convention: <em>positive</em> means {bName || 'B'} owes {aName || 'A'}; <em>negative</em> means {aName || 'A'} owes {bName || 'B'}.
          </p>
        </div>
      )}

      {/* Detailed timeline */}
      <div className="overflow-x-auto" data-testid="monthly-pair-rows">
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No activity for this pair in {data?.period?.start_date?.slice(0, 7) || 'this month'}.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/30">
                <tr><th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Kind</th>
                  <th className="text-left p-2">Description</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-right p-2">Running</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening row */}
                <tr className="border-t border-border/60 bg-zinc-50">
                  <td className="p-2 text-xs text-muted-foreground">{data?.period?.start_date}</td>
                  <td colSpan={3} className="p-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opening balance</td>
                  <td className="p-2 text-right tabular-nums font-semibold">{fmt(opening)}</td>
                </tr>
                {data.rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-border/40" data-testid={`monthly-pair-row-${i}`}>
                    <td className="p-2 text-xs text-muted-foreground">{(r.date || '').slice(0, 10)}</td>
                    <td className="p-2"><Badge variant="outline" className={`text-[10px] ${KIND_TONE[r.kind] || ''}`}>{KIND_LABEL[r.kind] || r.kind}</Badge></td>
                    <td className="p-2 text-xs truncate max-w-[300px]" title={r.description}>{r.description || r.category}</td>
                    <td className={`p-2 text-right tabular-nums text-xs ${r.delta < 0 ? 'text-error' : 'text-success'}`}>{r.delta > 0 ? '+' : ''}{fmt(r.delta)}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{fmt(r.running_balance)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-foreground/20 bg-zinc-50">
                  <td className="p-2 text-xs text-muted-foreground">{data?.period?.end_date}</td>
                  <td colSpan={3} className="p-2 text-xs font-bold uppercase tracking-wider">Closing balance</td>
                  <td className="p-2 text-right tabular-nums font-extrabold">{fmt(closing)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Matrix view ───────────────────────────────────────────────────────

function ScopeToggle({ scope, setScope }) {
  return (
    <div className="inline-flex rounded-lg border border-input p-0.5 bg-secondary/40" data-testid="matrix-scope-toggle">
      <button
        type="button"
        onClick={() => setScope('month')}
        className={`px-3 py-1 rounded text-xs font-medium ${scope === 'month' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}
        data-testid="matrix-scope-month"
      >This Month</button>
      <button
        type="button"
        onClick={() => setScope('all')}
        className={`px-3 py-1 rounded text-xs font-medium ${scope === 'all' ? 'bg-white shadow text-foreground' : 'text-muted-foreground'}`}
        data-testid="matrix-scope-all"
      >All Time</button>
    </div>
  );
}

function MatrixView({ loading, data, month, onReload, scope, setScope }) {
  if (loading) return <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>;
  if (!data?.matrix?.length) return (
    <div className="space-y-3" data-testid="monthly-matrix-view">
      <ScopeToggle scope={scope} setScope={setScope} />
      <div className="p-6 text-center text-sm text-muted-foreground">No branch data for this {scope === 'all' ? 'tenant' : 'month'}.</div>
    </div>
  );
  const branches = data.branches || [];
  const openPairs = (data.pairs || []).filter((p) => p.closing > 0.005);
  return (
    <div className="space-y-3" data-testid="monthly-matrix-view">
      <ScopeToggle scope={scope} setScope={setScope} />
      {/* Top stats + Run month-end auto-settle button */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary/60 border border-border">
            <Grid3x3 size={12} /> {data.totals?.branches} branches
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-warning/10 border border-warning/30 text-warning">
            <Wallet size={12} /> {data.totals?.open_pairs} open pair(s)
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-foreground text-background">
            {fmt(data.totals?.total_outstanding || 0)} total outstanding
          </span>
        </div>
        <RunMonthEndAutoSettleButton
          month={month}
          openPairs={openPairs}
          onDone={onReload}
        />
      </div>

      {/* Matrix */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/30">
            <tr>
              <th className="p-2 text-left sticky left-0 bg-secondary/30">From → To</th>
              {branches.map((b) => <th key={b.id} className="p-2 text-right" data-testid={`matrix-col-${b.id}`}>{b.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row) => (
              <tr key={row.branch_id} className="border-t border-border/40" data-testid={`matrix-row-${row.branch_id}`}>
                <td className="p-2 font-semibold sticky left-0 bg-white">{row.branch_name}</td>
                {row.cells.map((cell, i) => {
                  if (cell.balance == null) {
                    return <td key={i} className="p-2 text-right tabular-nums text-xs text-muted-foreground">—</td>;
                  }
                  const bal = Number(cell.balance || 0);
                  const tone = bal > 0.01 ? 'text-emerald-700 font-bold' : (bal < -0.01 ? 'text-rose-700 font-bold' : 'text-muted-foreground');
                  return (
                    <td
                      key={i}
                      className={`p-2 text-right tabular-nums text-xs ${tone}`}
                      title={`Opening ${fmt(cell.opening)} · Δ ${fmt(cell.in_month_delta)} · ${cell.row_count} row(s)`}
                      data-testid={`matrix-cell-${row.branch_id}-${cell.branch_id}`}
                    >
                      {bal > 0.01 ? '+' : (bal < -0.01 ? '−' : '')}{fmt(Math.abs(bal))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-muted-foreground leading-relaxed pt-2">
        <strong>Reading the matrix:</strong> each cell <code>(row=A, col=B)</code> is signed from A&apos;s perspective.
        A <span className="text-emerald-700 font-semibold">positive (+)</span> value means <em>B still owes A</em> at month end.
        A <span className="text-rose-700 font-semibold">negative (−)</span> value means <em>A still owes B</em>. Cells are symmetric — <code>(A,B)</code> and <code>(B,A)</code> are equal and opposite.
      </p>

      {/* Open-pair shortlist */}
      {openPairs.length > 0 && (
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Open pairs (need settlement)</p>
          <div className="space-y-1.5">
            {openPairs.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-warning/5" data-testid={`matrix-open-pair-${i}`}>
                <Badge variant="outline" className="text-xs bg-error/10 text-error border-error/30">{p.from_name}</Badge>
                <span className="text-xs text-muted-foreground">owes</span>
                <ArrowRight size={12} className="text-muted-foreground" />
                <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">{p.to_name}</Badge>
                <span className="ml-auto text-sm font-bold text-warning tabular-nums">{fmt(p.closing)}</span>
                {p.opening > 0 && <span className="text-[10px] text-muted-foreground">(opening was {fmt(p.opening)})</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, tone, big, testId, sub }) {
  const tones = {
    pos: 'text-emerald-700', neg: 'text-rose-700', zero: 'text-muted-foreground', info: 'text-foreground',
  };
  return (
    <div className={`p-3 rounded-lg border bg-white ${big ? 'border-2 border-foreground/20' : 'border-border'}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 ${big ? 'text-xl font-extrabold' : 'text-lg font-bold'} tabular-nums ${tones[tone] || tones.info}`}>{fmt(value)}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5 truncate" data-testid={testId ? `${testId}-mode` : undefined}>{sub}</p>}
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{hint}</p>}
    </div>
  );
}

// ── Run month-end auto-settle (iter707) ───────────────────────────────
// One-click bulk close of every open pair in the picked month.
// Re-uses the existing `/reports/inter-branch/settle-all` backend which
// loops over `netted_balances` and posts one settlement + one balanced
// JE per pair. We pre-scope to the month so the operator can confidently
// "close the books" for that period.
function RunMonthEndAutoSettleButton({ month, openPairs, onDone }) {
  const [open, setOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');
  // Default the settlement date to the LAST day of the picked month so
  // the JEs are stamped inside the same period (matches accountants'
  // mental model of "close at month end").
  const lastDayOfMonth = useMemo(() => {
    if (!month) return '';
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m, 0).getDate();
    return `${month}-${String(d).padStart(2, '0')}`;
  }, [month]);
  const [settleDate, setSettleDate] = useState(lastDayOfMonth);
  useEffect(() => { setSettleDate(lastDayOfMonth); }, [lastDayOfMonth]);

  const total = openPairs.reduce((s, p) => s + Number(p.closing || 0), 0);

  const { settle, posting } = useSettleAll({
    label: 'pair',
    contextName: month,
    fmtAmount: fmt,
    onDone: () => {
      setOpen(false);
      if (typeof onDone === 'function') onDone();
    },
  });

  const handleRun = () => {
    if (openPairs.length === 0) return;
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return settle(
      {
        start_date: `${month}-01`,
        end_date:   `${month}-${String(last).padStart(2, '0')}`,
        payment_mode: paymentMode,
        date: settleDate,
        notes: `Month-end auto-settle for ${month}`,
      },
      openPairs.length,
    );
  };

  return (
    <>
      <Button
        size="sm"
        disabled={openPairs.length === 0}
        onClick={() => setOpen(true)}
        className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
        data-testid="month-end-autosettle-btn"
        title={openPairs.length === 0 ? `${month} is already fully reconciled — nothing to settle` : `Auto-settle ${openPairs.length} open pair${openPairs.length === 1 ? '' : 's'} for ${month}`}
      >
        <Sparkles size={14} className="mr-1" /> Run month-end auto-settle
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="month-end-autosettle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-indigo-600" /> Close {month}?</DialogTitle>
            <DialogDescription>
              This posts <b>{openPairs.length}</b> settlement{openPairs.length === 1 ? '' : 's'} + <b>{openPairs.length}</b> balanced journal entr{openPairs.length === 1 ? 'y' : 'ies'} — one per open pair below — totalling <b>{fmt(total)}</b>. Existing transactions stay untouched; each pair&apos;s closing balance resets to zero from the settlement date forward.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-border max-h-[240px] overflow-y-auto">
              {openPairs.map((p, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs border-b last:border-b-0" data-testid={`month-end-row-${i}`}>
                  <span className="font-semibold text-error">{p.from_name}</span>
                  <ArrowRight size={11} className="text-muted-foreground shrink-0" />
                  <span className="font-semibold text-success">{p.to_name}</span>
                  <span className="ml-auto font-bold tabular-nums">{fmt(p.closing)}</span>
                  {p.opening > 0 && (
                    <span className="text-[10px] text-muted-foreground">(open {fmt(p.opening)} + Δ{fmt(p.in_month_delta || 0)})</span>
                  )}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Payment mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger className="mt-1" data-testid="month-end-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="other">Other / Clearing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Settlement date</Label>
                <Input
                  type="date"
                  value={settleDate}
                  onChange={(e) => setSettleDate(e.target.value)}
                  className="mt-1"
                  data-testid="month-end-date"
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              All pairs are stamped with the same payment mode + date.
              For a per-pair mode, use the legacy <em>Settle</em> button on each row above the monthly view.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={posting}>Cancel</Button>
            <Button
              onClick={handleRun}
              disabled={posting || openPairs.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="month-end-autosettle-confirm"
            >
              {posting ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
              {posting ? 'Closing month…' : `Close ${month}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Trend view (iter845s) ─────────────────────────────────────────────
// A 12-month rolling line of closing balances per branch pair. Positive
// = the "from" side is owed. Renders a compact SVG sparkline per pair
// so managers can spot slow-payers in one glance.
//
// Note: uses `formatCurrency`-aware `fmt()` from the top of this file.

function TrendView({ loading, data, onReload }) {
  const [expanded, setExpanded] = useState(null); // pair key of the expanded row

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="trend-loading">
        <Loader2 size={16} className="animate-spin mr-2" /> Building 12-month trend…
      </div>
    );
  }
  if (!data || !data.pairs?.length) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm" data-testid="trend-empty">
        No active branch pairs in the last 12 months. Cross-branch transactions will start populating this view automatically.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="trend-view">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {data.pairs.length} branch pair{data.pairs.length === 1 ? '' : 's'} · window {data.months[0]} → {data.months[data.months.length - 1]}
        </div>
        <Button size="sm" variant="ghost" onClick={onReload} data-testid="trend-refresh">
          <Loader2 size={12} className={`mr-1 ${loading ? 'animate-spin' : 'hidden'}`} />
          Refresh
        </Button>
      </div>
      <div className="border rounded-xl overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1fr)_120px_100px_1fr_40px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider bg-secondary/40 font-bold text-muted-foreground">
          <div>Pair</div>
          <div className="text-right">Current</div>
          <div className="text-right">Peak</div>
          <div>Trend (last 12 months)</div>
          <div></div>
        </div>
        {data.pairs.map((p, idx) => {
          const key = `${p.from_id}-${p.to_id}`;
          const isExpanded = expanded === key;
          return (
            <div key={key} className={`border-t ${idx === 0 ? 'border-t-0' : ''}`}>
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : key)}
                className="w-full grid grid-cols-[minmax(0,1fr)_120px_100px_1fr_40px] gap-2 px-3 py-2 items-center hover:bg-secondary/30 text-left"
                data-testid={`trend-row-${key}`}
              >
                <div className="text-xs">
                  <div className="font-semibold truncate">{p.from_name} <ArrowRight size={11} className="inline mx-0.5 text-muted-foreground" /> {p.to_name}</div>
                </div>
                <div className={`text-right tabular-nums text-xs font-bold ${p.current_balance > 0 ? 'text-emerald-700' : p.current_balance < 0 ? 'text-rose-700' : 'text-muted-foreground'}`}>
                  {fmt(Math.abs(p.current_balance))}
                </div>
                <div className="text-right tabular-nums text-xs text-muted-foreground">{fmt(p.peak_balance)}</div>
                <div>
                  <Sparkline points={p.points} />
                </div>
                <ChevronDown size={13} className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
              {isExpanded && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 text-[10px]">
                    {p.points.map((pt) => (
                      <div key={pt.month} className="border rounded p-1.5 bg-secondary/20 text-center">
                        <div className="text-muted-foreground">{pt.month}</div>
                        <div className={`font-semibold tabular-nums mt-0.5 ${pt.balance > 0.005 ? 'text-emerald-700' : pt.balance < -0.005 ? 'text-rose-700' : 'text-muted-foreground'}`}>
                          {pt.balance ? fmt(Math.abs(pt.balance)) : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Sparkline({ points, w = 200, h = 32 }) {
  if (!points?.length) return null;
  const vals = points.map((p) => p.balance || 0);
  const mn = Math.min(0, ...vals);
  const mx = Math.max(0, ...vals);
  const span = Math.max(1, mx - mn);
  const pad = 2;
  const step = (w - pad * 2) / Math.max(1, vals.length - 1);
  const yFor = (v) => h - pad - ((v - mn) / span) * (h - pad * 2);
  const zeroY = yFor(0);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * step} ${yFor(v)}`).join(' ');
  const last = vals[vals.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="text-indigo-600" preserveAspectRatio="none">
      {/* zero baseline */}
      <line x1={0} x2={w} y1={zeroY} y2={zeroY} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="2 3" />
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pad + (vals.length - 1) * step} cy={yFor(last)} r="2.5" fill={last > 0 ? '#059669' : last < 0 ? '#e11d48' : '#94a3b8'} />
    </svg>
  );
}

